import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  ArrowLeft,
  UploadCloud,
  Play,
  Pause,
  RotateCw,
  ShieldAlert,
  Loader2,
  Phone,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { scheduleSummary } from "../lib/schedule";
import LeadDispositionBadge from "../components/LeadDispositionBadge";
import ProspectDetailDrawer from "../components/ProspectDetailDrawer";

const StatusDot = ({ status }) => {
  const map = {
    running: "bg-emerald-400",
    paused: "bg-amber-400",
    draft: "bg-white/30",
    ended: "bg-zinc-500",
  };
  return (
    <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-white/60">
      <span
        className={`w-1.5 h-1.5 rounded-full ${map[status] || "bg-white/30"} ${
          status === "running" ? "animate-pulse" : ""
        }`}
      />
      {status}
    </span>
  );
};

export default function CampaignDetail() {
  const { id } = useParams();
  const [camp, setCamp] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openProspectId, setOpenProspectId] = useState(null);
  const fileRef = useRef();

  // Patch a prospect in-place (avoid full reload after disposition change).
  const replaceProspect = (updated) => {
    setProspects((list) => list.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
  };

  const load = async () => {
    try {
      const [c, p, s] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/prospects`),
        api.get(`/campaigns/${id}/stats`),
      ]);
      setCamp(c.data);
      setProspects(p.data.items || []);
      setStats(s.data);
    } catch (e) {
      toast.error("Could not load campaign");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [id]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/campaigns/${id}/upload-csv`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const hdrNote = data.header_row_index
        ? ` · headers on row ${data.header_row_index}`
        : "";
      toast.success(
        `Uploaded ${data.inserted} prospects · ${data.flagged_dnc} DNC flagged · ${data.skipped} skipped${hdrNote}`
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const launch = async () => {
    setBusy(true);
    try {
      await api.post(`/campaigns/${id}/launch`);
      toast.success("Campaign launched — Ashley is dialing");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Launch failed");
    } finally {
      setBusy(false);
    }
  };

  const pause = async () => {
    setBusy(true);
    try {
      await api.post(`/campaigns/${id}/pause`);
      toast.success("Paused");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    setBusy(true);
    try {
      await api.post(`/campaigns/${id}/resume`);
      toast.success("Resumed");
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (!camp) return <div className="text-white/40">Loading…</div>;

  const pct = stats?.total_prospects
    ? Math.round((stats.called / stats.total_prospects) * 100)
    : 0;

  return (
    <div data-testid="campaign-detail-page">
      <Link
        to="/campaigns"
        className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-white/40 hover:text-white mb-6"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to campaigns
      </Link>

      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <StatusDot status={camp.status} />
          </div>
          <h1 className="text-3xl font-medium tracking-tight text-white" data-testid="campaign-name-heading">
            {camp.name}
          </h1>
          <div className="text-xs text-white/40 font-mono mt-2">
            {camp.timezone} · {scheduleSummary(camp.schedule)} · max {camp.max_attempts} attempts
          </div>
        </div>
        <div className="flex gap-2">
          {camp.status === "draft" && (
            <button
              data-testid="campaign-launch-btn"
              onClick={launch}
              disabled={busy || !stats?.total_prospects || stats?.pending === 0}
              className="bg-[#FFB000] hover:bg-[#FFC133] text-black font-medium text-sm px-4 py-2 rounded-md transition-colors shadow-[0_0_18px_rgba(255,176,0,0.18)] disabled:opacity-50 flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Launch
            </button>
          )}
          {camp.status === "running" && (
            <button
              data-testid="campaign-pause-btn"
              onClick={pause}
              disabled={busy}
              className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 font-medium text-sm px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            >
              <Pause className="w-4 h-4" /> Pause
            </button>
          )}
          {camp.status === "paused" && (
            <button
              data-testid="campaign-resume-btn"
              onClick={resume}
              disabled={busy}
              className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-medium text-sm px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            >
              <RotateCw className="w-4 h-4" /> Resume
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="campaign-stats-row">
        {[
          { label: "Total", v: stats?.total_prospects, color: "text-white" },
          { label: "Called", v: stats?.called, color: "text-white" },
          { label: "Pending", v: stats?.pending, color: "text-white" },
          { label: "Booked", v: stats?.booked, color: "text-emerald-400" },
          { label: "Hot Leads", v: stats?.hot_leads, color: "text-rose-400" },
          { label: "DNC", v: stats?.dnc, color: "text-red-500" },
        ].map((s) => (
          <div key={s.label} className="bg-[#121214] border border-white/10 rounded-lg p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">
              {s.label}
            </div>
            <div className={`font-mono text-2xl mt-1 tracking-tighter ${s.color}`}>
              {s.v ?? 0}
            </div>
          </div>
        ))}
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-mono uppercase tracking-widest text-white/40">
            Campaign Progress
          </div>
          <div className="text-xs font-mono text-white/60">{pct}% dialed</div>
        </div>
        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#FFB000] to-[#FFC133] rounded-full transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* CSV Upload */}
      <div className="mb-8">
        <label
          htmlFor="csv-upload"
          data-testid="csv-dropzone"
          className="border-2 border-dashed border-white/10 rounded-xl p-10 text-center hover:border-[#FFB000]/50 hover:bg-[#FFB000]/5 transition-all cursor-pointer group block bg-[#0a0a0b]"
        >
          <UploadCloud className="w-8 h-8 text-white/30 group-hover:text-[#FFB000] mx-auto mb-3 transition-colors" strokeWidth={1.5} />
          <div className="text-sm text-white/80 font-medium">
            {uploading ? "Scrubbing DNC and uploading…" : "Drop CSV / Excel or click to upload"}
          </div>
          <div className="text-xs text-white/40 mt-1.5 font-mono">
            Accepts .csv, .xlsx, .xls · Required column: phone · Optional: name, company, email, notes
          </div>
          <input
            id="csv-upload"
            ref={fileRef}
            data-testid="csv-upload-input"
            type="file"
            accept=".csv,.xlsx,.xls,.xlsm,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={onFile}
            className="hidden"
          />
        </label>
      </div>

      {/* Prospects table */}
      <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="text-sm font-medium text-white">Prospect List</div>
          <div className="text-xs font-mono text-white/40">
            {prospects.length} rows
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="prospects-table">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                <th className="text-left px-5 py-3 font-normal">Name</th>
                <th className="text-left px-5 py-3 font-normal">Phone</th>
                <th className="text-left px-5 py-3 font-normal">Company</th>
                <th className="text-left px-5 py-3 font-normal">Disposition</th>
                <th className="text-left px-5 py-3 font-normal">Status</th>
                <th className="text-right px-5 py-3 font-normal">Attempts</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {prospects.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-white/40 text-sm">
                    No prospects yet — upload a CSV above.
                  </td>
                </tr>
              )}
              {prospects.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`prospect-row-${p.id}`}
                  onClick={() => setOpenProspectId(p.id)}
                  className={`cursor-pointer hover:bg-white/[0.02] ${p.is_dnc ? "bg-red-500/5" : ""}`}
                >
                  <td className="px-5 py-3 text-white/90">{p.name || "—"}</td>
                  <td className="px-5 py-3 font-mono text-white/80">
                    <div className="flex items-center gap-2">
                      {p.is_dnc && (
                        <span data-testid={`prospect-dnc-flag-${p.id}`} title="DNC">
                          <ShieldAlert className="w-3.5 h-3.5 text-red-500" />
                        </span>
                      )}
                      <span className={p.is_dnc ? "text-red-400 line-through" : ""}>
                        {p.phone_normalized}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-white/60">{p.company || "—"}</td>
                  <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                    <LeadDispositionBadge
                      prospectId={p.id}
                      value={p.disposition}
                      onUpdate={replaceProspect}
                    />
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider ${
                        p.is_dnc
                          ? "text-red-500"
                          : p.status === "called"
                          ? "text-emerald-400"
                          : p.status === "calling"
                          ? "text-[#FFB000]"
                          : "text-white/40"
                      }`}
                    >
                      {p.status === "calling" && <Phone className="w-3 h-3 animate-pulse" />}
                      {p.status === "called" && <CheckCircle2 className="w-3 h-3" />}
                      {p.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-white/60">
                    {p.attempts}/{camp.max_attempts}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {openProspectId && (
        <ProspectDetailDrawer
          prospectId={openProspectId}
          onClose={() => setOpenProspectId(null)}
          onUpdate={replaceProspect}
        />
      )}
    </div>
  );
}
